import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Inbox, CalendarClock, AlertOctagon, CheckCircle2, UserCircle2,
  Users as UsersIcon, Archive, Search, Plus, ChevronRight, Bell, Send,
  Moon, Sun, Filter, ListTodo, TrendingUp, Clock, AlarmClock,
  Settings, Trash2, KeyRound, FolderPlus, GripVertical, X, Star, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskDetailPanel from '../../components/todo/TaskDetailPanel';

// --- Constants ---
const PRIORITY_DOTS = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-slate-400' };
const PRIORITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };
const BUSINESS_NAME = 'Shri Kripa';
const TODAY = () => new Date().toISOString().slice(0, 10);

// MS To-Do style per-view themes — gradient backdrops + greeting + emoji
const VIEW_THEMES = {
  pending: {
    label: 'All Pending',
    emoji: '☀️',
    sub: 'Let’s get things done',
    bg: 'bg-gradient-to-br from-amber-100 via-orange-50 to-rose-50',
    bgDark: 'bg-gradient-to-br from-amber-950/60 via-orange-950/30 to-slate-950',
  },
  overdue: {
    label: 'Overdue',
    emoji: '⏰',
    sub: 'Catch up on what slipped past',
    bg: 'bg-gradient-to-br from-rose-100 via-pink-50 to-amber-50',
    bgDark: 'bg-gradient-to-br from-rose-950/60 via-pink-950/30 to-slate-950',
  },
  upcoming: {
    label: 'Upcoming',
    emoji: '🗓️',
    sub: 'Plan ahead, stay on top',
    bg: 'bg-gradient-to-br from-sky-100 via-indigo-50 to-violet-50',
    bgDark: 'bg-gradient-to-br from-sky-950/60 via-indigo-950/30 to-slate-950',
  },
  completed: {
    label: 'Completed',
    emoji: '✅',
    sub: 'Look at all you’ve done',
    bg: 'bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-50',
    bgDark: 'bg-gradient-to-br from-emerald-950/60 via-teal-950/30 to-slate-950',
  },
  archived: {
    label: 'Archived',
    emoji: '📦',
    sub: 'Older completed tasks',
    bg: 'bg-gradient-to-br from-slate-100 via-stone-50 to-slate-100',
    bgDark: 'bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900',
  },
};

const greetingFor = (date = new Date()) => {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const longDateLabel = (date = new Date()) =>
  date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

// --- Utilities ---
const formatDue = (d, t) => {
  if (!d) return '';
  const dateObj = new Date(d + (t ? `T${t}` : ''));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tom = new Date(today); tom.setDate(tom.getDate() + 1);
  const dOnly = new Date(d); dOnly.setHours(0, 0, 0, 0);
  let label;
  if (dOnly.getTime() === today.getTime()) label = 'Today';
  else if (dOnly.getTime() === tom.getTime()) label = 'Tomorrow';
  else label = dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (t) label += `, ${t}`;
  return label;
};

const isOverdue = (task) => {
  if (!task.due_date || task.status !== 'pending') return false;
  return task.due_date < TODAY();
};

const buildWhatsappMessage = (task, kind) => {
  const due = task.due_date ? formatDue(task.due_date, task.due_time) : 'No due date';
  const lastNote = (task.notes || []).slice(-1)[0]?.text || '';
  const intro = kind === 'overdue'
    ? `⚠️ OVERDUE TASK`
    : kind === 'assignment'
    ? `📌 NEW TASK ASSIGNED`
    : `🔔 TASK REMINDER`;
  return [
    `${intro}`,
    ``,
    `Hello ${task.assigned_to_name},`,
    `You have a pending task: *${task.title}*`,
    task.description ? `Details: ${task.description}` : null,
    lastNote ? `Notes: ${lastNote}` : null,
    `Priority: ${PRIORITY_LABEL[task.priority] || 'Medium'}`,
    `Due: ${due}`,
    ``,
    `— ${BUSINESS_NAME}`,
  ].filter(Boolean).join('\n');
};

// --- Sidebar item ---
const SidebarItem = ({ icon: Icon, label, count, active, onClick, dark, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all ${
      active
        ? (dark ? 'bg-blue-500/15 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium')
        : (dark ? 'text-slate-300 hover:bg-slate-800/60' : 'text-slate-700 hover:bg-slate-100')
    }`}
  >
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span className="flex-1 text-left truncate">{label}</span>
    {typeof count === 'number' && count > 0 && (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
        active ? (dark ? 'bg-blue-500/30 text-blue-100' : 'bg-blue-200 text-blue-800') : (dark ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700')
      }`}>{count}</span>
    )}
  </button>
);

// --- Stat card ---
const StatCard = ({ icon: Icon, label, value, accent, dark }) => (
  <div className={`rounded-xl border p-3 transition-shadow hover:shadow-md ${
    dark ? 'bg-slate-900/70 border-slate-800' : 'bg-white border-slate-200'
  }`}>
    <div className="flex items-center justify-between">
      <div className={`text-[10px] uppercase tracking-wide font-semibold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
      <Icon className={`h-3.5 w-3.5 ${accent || 'text-slate-400'}`} />
    </div>
    <div className={`text-2xl font-bold mt-1 ${accent || (dark ? 'text-slate-100' : 'text-slate-900')}`}>{value}</div>
  </div>
);

// --- Task row card (MS To-Do inspired: solid white card on tinted backdrop) ---
const TaskRow = ({ task, onToggle, onClick, onWhatsapp, onSnooze, onStarToggle, dark, selected, listName }) => {
  const overdue = isOverdue(task);
  const subtle = dark ? 'text-slate-400' : 'text-slate-500';
  const starred = task.priority === 'high';

  return (
    <div
      onClick={() => onClick(task)}
      className={`group flex items-center gap-3.5 px-4 py-3 rounded-xl cursor-pointer transition-all backdrop-blur-sm ${
        selected
          ? (dark
              ? 'bg-slate-800 ring-1 ring-blue-500/40 shadow-md'
              : 'bg-white ring-1 ring-blue-200 shadow-md')
          : (dark
              ? 'bg-slate-900/80 hover:bg-slate-900 shadow-sm hover:shadow-md'
              : 'bg-white/95 hover:bg-white shadow-sm hover:shadow-md')
      }`}
      data-testid={`task-row-${task.id}`}
    >
      {/* Round MS-style checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(task); }}
        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
          task.status === 'completed'
            ? 'bg-blue-600 border-blue-600 text-white scale-100'
            : (dark
                ? 'border-slate-600 hover:border-blue-400 hover:scale-110'
                : 'border-slate-300 hover:border-blue-500 hover:scale-110')
        }`}
        data-testid={`task-toggle-${task.id}`}
      >
        {task.status === 'completed' && <span className="text-[11px] leading-none font-bold">✓</span>}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`text-sm font-medium truncate ${
            task.status === 'completed'
              ? (dark ? 'line-through opacity-50 text-slate-400' : 'line-through opacity-50 text-slate-500')
              : (dark ? 'text-slate-100' : 'text-slate-900')
          }`}>
            {task.title}
          </div>
          {(task.tags || []).slice(0, 3).map(t => (
            <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded-full ${dark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
              #{t}
            </span>
          ))}
        </div>

        <div className={`flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] ${subtle}`}>
          {listName && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${
              dark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-100 text-blue-700'
            }`} title="List">
              <FolderPlus className="h-2.5 w-2.5" />{listName}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <UserCircle2 className="h-3 w-3" />{task.assigned_to_name}
          </span>
          {task.due_date && (
            <span className={`inline-flex items-center gap-1 ${overdue ? 'text-rose-500 font-medium' : ''}`}>
              <CalendarClock className="h-3 w-3" />{formatDue(task.due_date, task.due_time)}
              {overdue && ' · Overdue'}
            </span>
          )}
          {task.repeat && task.repeat !== 'none' && (
            <span className="inline-flex items-center gap-1" title="Recurring task">
              ↻ {
                task.repeat.startsWith('custom:')
                  ? `every ${task.repeat.split(':')[1]}d`
                  : task.repeat
              }
            </span>
          )}
          {task.snoozed_until && new Date(task.snoozed_until) > new Date() && (
            <span className="inline-flex items-center gap-1 text-amber-500"><AlarmClock className="h-3 w-3" />Snoozed</span>
          )}
        </div>

        {(task.progress ?? 0) > 0 && task.status !== 'completed' && (
          <div className={`mt-2 h-1 w-full rounded-full overflow-hidden ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}>
            <div className="h-full bg-blue-500" style={{ width: `${task.progress}%` }} />
          </div>
        )}
      </div>

      {/* Star toggle (replaces priority dot — high = filled gold star) */}
      <button
        onClick={(e) => { e.stopPropagation(); onStarToggle(task); }}
        title={starred ? 'Remove importance' : 'Mark important'}
        className={`flex-shrink-0 p-1.5 rounded-md transition-all ${
          starred
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100'
        } ${dark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
        data-testid={`task-star-${task.id}`}
      >
        <Star className={`h-4 w-4 ${starred ? 'fill-amber-400 text-amber-400' : (dark ? 'text-slate-500' : 'text-slate-400')}`} />
      </button>

      {/* Hover actions */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); onWhatsapp(task, 'reminder'); }}
          title="WhatsApp reminder" className={`p-1.5 rounded-md ${dark ? 'hover:bg-slate-800 text-emerald-400' : 'hover:bg-slate-100 text-emerald-600'}`}>
          <Send className="h-3.5 w-3.5" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onSnooze(task); }}
          title="Snooze 1 day" className={`p-1.5 rounded-md ${dark ? 'hover:bg-slate-800 text-amber-400' : 'hover:bg-slate-100 text-amber-600'}`}>
          <AlarmClock className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// --- Sortable wrapper for drag-drop ---
const SortableTaskRow = ({ task, ...rest }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group/sortable">
      <button
        {...attributes} {...listeners}
        className="absolute -left-5 top-1/2 -translate-y-1/2 opacity-0 group-hover/sortable:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-700"
        title="Drag to reorder"
        data-testid={`drag-handle-${task.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <TaskRow task={task} {...rest} />
    </div>
  );
};

// =================================================================
//                      Main Page
// =================================================================
const TodoList = () => {
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [dashboard, setDashboard] = useState({ pending: 0, completed: 0, due_today: 0, overdue: 0, top_assignees: [] });
  const [loading, setLoading] = useState(true);

  const [activeView, setActiveView] = useState('pending'); // pending | today | overdue | upcoming | completed | mine | by:userId | archived
  const [searchQ, setSearchQ] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [tags, setTags] = useState([]);

  const [selectedTask, setSelectedTask] = useState(null);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickAssignee, setQuickAssignee] = useState('');
  const [quickDate, setQuickDate] = useState('');
  const [quickPriority, setQuickPriority] = useState('medium');
  const [quickRepeat, setQuickRepeat] = useState('none');
  const [quickCustomDays, setQuickCustomDays] = useState('');
  const [dark, setDark] = useState(false);

  const [currentUserId, setCurrentUserId] = useState(null);

  // V2 — Lists, settings dialog, dnd
  const [lists, setLists] = useState([]);
  const [showListDialog, setShowListDialog] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [editMemberId, setEditMemberId] = useState(null);
  const [memberForm, setMemberForm] = useState({ name: '', email: '', phone: '' });
  const [savingMember, setSavingMember] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // --- Load me ---
  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() })
      .then(r => setCurrentUserId(r.data.id))
      .catch(() => {});
  }, []);

  // --- Load core data ---
  const fetchAll = useCallback(async () => {
    try {
      const [tRes, uRes, byUserRes, dashRes, tagsRes, listsRes] = await Promise.all([
        axios.get(`${API}/todos?archived=${activeView === 'archived'}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/todos/by-user`, { headers: getAuthHeaders() }),
        axios.get(`${API}/todos/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API}/todos/tags`, { headers: getAuthHeaders() }),
        axios.get(`${API}/todo-lists`, { headers: getAuthHeaders() }),
      ]);
      setTodos(tRes.data);
      setUsers(uRes.data);
      setByUser(byUserRes.data);
      setDashboard(dashRes.data);
      setTags(tagsRes.data);
      setLists(listsRes.data);
      if (!quickAssignee && uRes.data[0]) setQuickAssignee(uRes.data[0].id);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- Filtered list ---
  const visibleTasks = useMemo(() => {
    let list = todos;
    if (activeView === 'pending') list = list.filter(t => t.status === 'pending');
    else if (activeView === 'completed') list = list.filter(t => t.status === 'completed');
    else if (activeView === 'overdue') list = list.filter(t => t.status === 'pending' && t.due_date && t.due_date < TODAY());
    else if (activeView === 'upcoming') list = list.filter(t => t.status === 'pending' && t.due_date && t.due_date > TODAY());
    else if (activeView === 'archived') list = todos; // backend already filtered archived=true
    else if (activeView.startsWith('by:')) {
      const uid = activeView.slice(3);
      list = list.filter(t => t.assigned_to === uid && t.status === 'pending');
    }
    else if (activeView.startsWith('list:')) {
      const lid = activeView.slice(5);
      list = list.filter(t => t.list_id === lid && t.status === 'pending');
    }
    if (filterPriority !== 'all') list = list.filter(t => (t.priority || 'medium') === filterPriority);
    if (filterTag !== 'all') list = list.filter(t => (t.tags || []).includes(filterTag));
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      list = list.filter(t =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.assigned_to_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [todos, activeView, filterPriority, filterTag, searchQ, currentUserId]);

  // --- Actions ---
  const toggle = async (task) => {
    try {
      await axios.put(`${API}/todos/${task.id}`, {}, { headers: getAuthHeaders() });
      toast.success(task.status === 'pending' ? 'Marked complete' : 'Reopened');
      fetchAll();
    } catch {
      toast.error('Failed');
    }
  };

  const snooze = async (task) => {
    try {
      await axios.post(`${API}/todos/${task.id}/snooze?until=tomorrow`, {}, { headers: getAuthHeaders() });
      toast.success('Snoozed until tomorrow');
      fetchAll();
    } catch {
      toast.error('Failed to snooze');
    }
  };

  // Toggle star — flips priority between high (starred) and medium (default).
  const toggleStar = async (task) => {
    const next = task.priority === 'high' ? 'medium' : 'high';
    try {
      await axios.put(`${API}/todos/${task.id}`, { priority: next }, { headers: getAuthHeaders() });
      // Optimistic local update
      setTodos((prev) => prev.map((t) => (t.id === task.id ? { ...t, priority: next } : t)));
      if (selectedTask?.id === task.id) {
        setSelectedTask({ ...selectedTask, priority: next });
      }
    } catch {
      toast.error('Failed to update');
    }
  };

  // --- Edit team member ---
  useEffect(() => {
    if (!editMemberId) return;
    const u = users.find(x => x.id === editMemberId);
    if (u) setMemberForm({ name: u.name || '', email: u.email || '', phone: u.phone || '' });
  }, [editMemberId, users]);

  const saveMember = async () => {
    if (!editMemberId) return;
    const name = (memberForm.name || '').trim();
    const email = (memberForm.email || '').trim();
    const phone = (memberForm.phone || '').replace(/[^0-9]/g, '');
    if (!name) { toast.error('Name is required'); return; }
    if (!email) { toast.error('Email is required'); return; }
    setSavingMember(true);
    try {
      const { data } = await axios.put(`${API}/users/${editMemberId}`,
        { name, email, phone },
        { headers: getAuthHeaders() }
      );
      // Refresh local users list
      setUsers((prev) => prev.map((u) => (u.id === editMemberId ? { ...u, ...data } : u)));
      toast.success(`Updated ${data.name}`);
      setEditMemberId(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error('That email is already used by another user');
      else toast.error(err?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingMember(false);
    }
  };

  const duplicate = async (task) => {
    try {
      const { data } = await axios.post(`${API}/todos/${task.id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success('Task duplicated');
      fetchAll();
      setSelectedTask(data);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await axios.delete(`${API}/todos/${task.id}`, { headers: getAuthHeaders() });
      toast.success('Deleted');
      setSelectedTask(null);
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const sendWhatsapp = (task, kind = 'reminder') => {
    const u = users.find(x => x.id === task.assigned_to);
    const phone = (u?.phone || '').replace(/[^0-9]/g, '');
    if (!phone) { toast.error(`No phone on file for ${task.assigned_to_name}`); return; }
    // Wassy is configured app-wide — fire to backend; fallback to wa.me on failure.
    axios.post(`${API}/todos/${task.id}/whatsapp?kind=${kind}`, {}, { headers: getAuthHeaders() })
      .then(() => toast.success(`Reminder sent to ${task.assigned_to_name} via WhatsApp`))
      .catch((e) => toast.error(e.response?.data?.detail || 'WhatsApp send failed — open manual link?', {
        action: { label: 'Open WhatsApp', onClick: () => openWaMeFallback(task, kind, phone) },
      }));
  };

  const openWaMeFallback = (task, kind, phone) => {
    const msg = buildWhatsappMessage(task, kind);
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // --- Lists CRUD ---
  const addList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    try {
      const { data } = await axios.post(`${API}/todo-lists`, { name: newListName.trim() }, { headers: getAuthHeaders() });
      setLists(prev => [...prev, data]);
      setNewListName('');
      setShowListDialog(false);
      toast.success('List created');
      setActiveView(`list:${data.id}`);
    } catch {
      toast.error('Failed to create list');
    }
  };

  const removeList = async (l) => {
    if (!window.confirm(`Delete list "${l.name}"? Tasks will remain but lose this list.`)) return;
    try {
      await axios.delete(`${API}/todo-lists/${l.id}`, { headers: getAuthHeaders() });
      setLists(prev => prev.filter(x => x.id !== l.id));
      if (activeView === `list:${l.id}`) setActiveView('pending');
      toast.success('List deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete list');
    }
  };

  // --- Drag-drop reorder ---
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = visibleTasks.findIndex(t => t.id === active.id);
    const newIdx = visibleTasks.findIndex(t => t.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(visibleTasks, oldIdx, newIdx);
    // Optimistic in-memory update
    const idMap = new Map(reordered.map((t, i) => [t.id, i]));
    setTodos(prev => prev.map(t => idMap.has(t.id) ? { ...t, order: idMap.get(t.id) } : t));
    try {
      const payload = reordered.map((t, i) => ({ id: t.id, order: i }));
      await axios.post(`${API}/todos/reorder`, payload, { headers: getAuthHeaders() });
    } catch {
      toast.error('Reorder failed — refreshing');
      fetchAll();
    }
  };

  const remindAllOverdue = () => {
    const overdue = todos.filter(isOverdue);
    if (overdue.length === 0) return toast.info('No overdue tasks');
    if (!window.confirm(`Send overdue reminder to ${overdue.length} task(s)?`)) return;
    overdue.forEach(t => sendWhatsapp(t, 'overdue'));
  };

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim() || !quickAssignee) return toast.error('Title and assignee required');
    let repeatVal = quickRepeat || 'none';
    if (repeatVal === 'custom') {
      const n = Math.max(1, parseInt(quickCustomDays, 10) || 0);
      if (!n) return toast.error('Custom interval (days) required');
      repeatVal = `custom:${n}`;
    }
    // If a list view is currently active, anchor the new task to that list.
    const listIdFromView = activeView.startsWith('list:') ? activeView.slice(5) : null;
    try {
      const { data } = await axios.post(`${API}/todos`, {
        title: quickTitle.trim(),
        assigned_to: quickAssignee,
        due_date: quickDate || null,
        priority: quickPriority,
        repeat: repeatVal,
        list_id: listIdFromView,
      }, { headers: getAuthHeaders() });
      toast.success(listIdFromView
        ? `Task added to "${lists.find(l => l.id === listIdFromView)?.name || 'list'}"`
        : 'Task added');
      setQuickTitle(''); setQuickDate('');
      setQuickRepeat('none'); setQuickCustomDays('');
      setSelectedTask(data);
      fetchAll();
      const u = users.find(x => x.id === quickAssignee);
      if (u?.phone) {
        toast(`Notify ${u.name} on WhatsApp?`, {
          action: { label: 'Send', onClick: () => sendWhatsapp(data, 'assignment') },
          duration: 6000,
        });
      }
    } catch {
      toast.error('Failed to create task');
    }
  };

  const addMemberInline = async () => {
    const name = window.prompt('New team member name:');
    if (!name || !name.trim()) return;
    const phone = window.prompt(`WhatsApp phone for ${name.trim()} (10 digits, no +91):`) || '';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const email = window.prompt(`Email for ${name.trim()} (used for login):`) || '';
    if (!email.trim()) { toast.error('Email is required'); return; }
    try {
      const params = new URLSearchParams({
        name: name.trim(),
        email: email.trim(),
        phone: cleanPhone,
        role: 'member',
      }).toString();
      const { data } = await axios.post(`${API}/users/add-member?${params}`, {}, { headers: getAuthHeaders() });
      toast.success(`${data.user.name} added · default password: ${data.default_password}`);
      // Refresh users and auto-select the new member
      const fresh = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(fresh.data);
      setQuickAssignee(data.user.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add team member');
    }
  };

  const onChanged = (updated) => {
    setTodos(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    setSelectedTask(updated);
  };

  // --- Theme classes ---
  const root = dark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900';
  const sidebarCls = dark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200';
  const mainCls = dark ? 'bg-slate-950' : 'bg-slate-50';

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96 text-slate-500">Loading tasks...</div></Layout>;
  }

  // --- Sidebar definition ---
  const sidebarSections = [
    { id: 'pending', icon: Inbox, label: 'All Pending', count: dashboard.pending, testid: 'view-pending' },
    { id: 'overdue', icon: AlertOctagon, label: 'Overdue', count: dashboard.overdue, testid: 'view-overdue' },
    { id: 'upcoming', icon: Clock, label: 'Upcoming', testid: 'view-upcoming' },
    { id: 'completed', icon: CheckCircle2, label: 'Completed', count: dashboard.completed, testid: 'view-completed' },
    { id: 'archived', icon: Archive, label: 'Archived', testid: 'view-archived' },
  ];

  const headerLabel = (() => {
    const found = sidebarSections.find(s => s.id === activeView);
    if (found) return found.label;
    if (activeView.startsWith('by:')) {
      const u = users.find(x => x.id === activeView.slice(3));
      return u ? `Tasks for ${u.name}` : 'Team Tasks';
    }
    return 'Tasks';
  })();

  return (
    <Layout>
      <div className={`-mx-6 -my-8 min-h-[calc(100vh-4rem)] flex ${root}`} data-testid="todo-page">
        {/* ============ Sidebar ============ */}
        <div className={`w-64 flex-shrink-0 border-r ${sidebarCls} sticky top-0 h-screen overflow-y-auto`}>
          <div className="px-5 py-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold tracking-tight">Tasks</h2>
              <button onClick={() => setDark(d => !d)} className={`p-1.5 rounded-md ${dark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`} title="Toggle theme" data-testid="theme-toggle">
                {dark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-slate-600" />}
              </button>
            </div>
            <div className={`text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`}>Smart task management</div>
          </div>

          {/* Stat strip */}
          <div className="px-3 grid grid-cols-3 gap-2 mb-4">
            <StatCard icon={ListTodo} label="Pending" value={dashboard.pending} accent="text-blue-600" dark={dark} />
            <StatCard icon={CheckCircle2} label="Done" value={dashboard.completed} accent="text-emerald-600" dark={dark} />
            <StatCard icon={AlertOctagon} label="Overdue" value={dashboard.overdue} accent="text-rose-600" dark={dark} />
          </div>

          {/* Views */}
          <div className="px-2 space-y-0.5 mb-4">
            {sidebarSections.map(s => (
              <SidebarItem key={s.id}
                icon={s.icon} label={s.label} count={s.count}
                active={activeView === s.id}
                onClick={() => setActiveView(s.id)}
                dark={dark} testid={s.testid}
              />
            ))}
          </div>

          {/* Lists */}
          <div className="px-2 mb-4">
            <div className={`flex items-center justify-between px-3 py-1.5 text-[11px] uppercase tracking-wide font-semibold ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              <span className="flex items-center gap-1.5"><FolderPlus className="h-3 w-3" />Lists</span>
              <button onClick={() => setShowListDialog(true)}
                className={`p-0.5 rounded ${dark ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`}
                title="Create list" data-testid="add-list-btn">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="space-y-0.5">
              {lists.length === 0 && (
                <div className={`px-3 py-1 text-[11px] italic ${dark ? 'text-slate-600' : 'text-slate-400'}`}>No lists yet.</div>
              )}
              {lists.map(l => {
                const count = todos.filter(t => t.list_id === l.id && t.status === 'pending').length;
                const active = activeView === `list:${l.id}`;
                return (
                  <div key={l.id} className="flex items-stretch group/list">
                    <SidebarItem
                      icon={ListTodo} label={l.name} count={count}
                      active={active}
                      onClick={() => setActiveView(`list:${l.id}`)}
                      dark={dark} testid={`list-${l.id}`}
                    />
                    <button
                      onClick={() => removeList(l)}
                      className={`opacity-0 group-hover/list:opacity-100 px-1.5 text-rose-500 ${dark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} rounded-md`}
                      title="Delete list"
                      data-testid={`delete-list-${l.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Team */}
          {byUser.length > 0 && (
            <div className="px-2 mb-4">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wide font-semibold ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                <UsersIcon className="h-3 w-3" />Team Tasks
              </div>
              <div className="space-y-0.5">
                {byUser.slice(0, 8).map(u => {
                  const active = activeView === `by:${u.user_id}`;
                  return (
                    <div key={u.user_id} className="group/team flex items-center">
                      <button
                        onClick={() => setActiveView(`by:${u.user_id}`)}
                        className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                          active
                            ? (dark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700')
                            : (dark ? 'text-slate-300 hover:bg-slate-800/60' : 'text-slate-700 hover:bg-slate-100')
                        }`}
                        data-testid={`team-${u.user_id}`}
                      >
                        <UserCircle2 className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left truncate">{u.name}</span>
                        <span className={`text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{u.count}</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditMemberId(u.user_id); }}
                        className={`opacity-0 group-hover/team:opacity-100 mr-1 p-1.5 rounded-md transition ${
                          dark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-200 text-slate-500'
                        }`}
                        title={`Edit ${u.name}`}
                        data-testid={`edit-member-${u.user_id}`}
                      >
                        <Settings className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ============ Main pane (MS To-Do style: gradient backdrop) ============ */}
        <div
          className={`flex-1 flex flex-col min-w-0 relative ${
            dark
              ? (VIEW_THEMES[activeView]?.bgDark || 'bg-slate-950')
              : (VIEW_THEMES[activeView]?.bg || 'bg-slate-50')
          }`}
          data-testid="todo-main-pane"
        >
          {/* Hero greeting header */}
          <div className={`px-8 pt-8 pb-3 flex items-end justify-between gap-4 flex-wrap ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-3xl font-heading tracking-tight">
                <span className="text-3xl" data-testid="view-emoji">
                  {VIEW_THEMES[activeView]?.emoji || '📋'}
                </span>
                <span data-testid="view-headline">
                  {activeView === 'pending'
                    ? `${greetingFor()}, ${users.find(u => u.id === currentUserId)?.name?.split(' ')[0] || 'there'}!`
                    : (VIEW_THEMES[activeView]?.label || headerLabel)}
                </span>
              </div>
              <div className={`mt-1 text-sm ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
                {longDateLabel()} ·{' '}
                <span className={dark ? 'text-slate-300' : 'text-slate-700'}>
                  {visibleTasks.length} {visibleTasks.length === 1 ? 'task' : 'tasks'}
                </span>
                {VIEW_THEMES[activeView]?.sub && activeView === 'pending' && (
                  <span className="ml-2 italic opacity-70">· {VIEW_THEMES[activeView].sub}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => setShowSettings(true)}
                variant="ghost" size="sm"
                className={`rounded-full ${dark ? 'text-slate-300 hover:bg-slate-800/60' : 'text-slate-700 hover:bg-white/70'}`}
                data-testid="open-settings-btn"
              >
                <Settings className="h-3.5 w-3.5 mr-1.5" />Reminder Setup
              </Button>
              <Button
                onClick={remindAllOverdue}
                variant="ghost" size="sm"
                className={`rounded-full ${dark ? 'text-slate-300 hover:bg-slate-800/60' : 'text-slate-700 hover:bg-white/70'}`}
                data-testid="bulk-remind-overdue"
              >
                <Bell className="h-3.5 w-3.5 mr-1.5" />Remind Overdue
              </Button>
            </div>
          </div>

          {/* Slim search/filter strip (no border, blends with backdrop) */}
          <div className="px-8 py-2 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className={`h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 ${dark ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input
                value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search tasks…"
                className={`pl-9 h-9 rounded-full border-transparent ${
                  dark
                    ? 'bg-slate-900/60 text-slate-100 placeholder-slate-500 focus-visible:ring-1 focus-visible:ring-slate-700'
                    : 'bg-white/70 backdrop-blur placeholder-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300'
                }`}
                data-testid="search-input"
              />
            </div>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger
                className={`w-32 h-9 rounded-full border-transparent ${dark ? 'bg-slate-900/60' : 'bg-white/70 backdrop-blur'}`}
                data-testid="filter-priority"
              >
                <Filter className="h-3 w-3 mr-1.5 inline" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            {tags.length > 0 && (
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className={`w-32 h-9 rounded-full border-transparent ${dark ? 'bg-slate-900/60' : 'bg-white/70 backdrop-blur'}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {tags.map(t => <SelectItem key={t} value={t}>#{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Task list */}
          <div className="flex-1 overflow-y-auto px-8 pt-4 pb-32">
            {visibleTasks.length === 0 ? (
              <div className={`text-center py-20 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ background: 'rgba(255,255,255,0.5)' }}>
                  <Sparkles className={`h-7 w-7 ${dark ? 'text-slate-500' : 'text-slate-400'}`} />
                </div>
                <div className="text-sm font-medium">
                  {activeView === 'completed' ? 'Nothing finished here yet' :
                   activeView === 'overdue' ? 'You’re all caught up — nothing overdue' :
                   activeView === 'archived' ? 'Archive is empty' :
                   'A fresh start awaits'}
                </div>
                <div className="text-xs mt-1 opacity-70">
                  {activeView === 'completed' || activeView === 'archived' ? '' : 'Add a task below to begin.'}
                </div>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={visibleTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2 pl-5 max-w-3xl mx-auto">
                    {visibleTasks.map(t => (
                      <SortableTaskRow key={t.id}
                        task={t}
                        listName={lists.find(l => l.id === t.list_id)?.name || null}
                        onToggle={toggle}
                        onClick={setSelectedTask}
                        onWhatsapp={sendWhatsapp}
                        onSnooze={snooze}
                        onStarToggle={toggleStar}
                        selected={selectedTask?.id === t.id}
                        dark={dark}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Floating Add-a-task bar (MS To-Do style: pinned at bottom of pane) */}
          {activeView !== 'completed' && activeView !== 'archived' && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[min(880px,90%)] z-10">
              <form
                onSubmit={quickAdd}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-xl ring-1 backdrop-blur-md ${
                  dark
                    ? 'bg-slate-900/90 ring-slate-800'
                    : 'bg-white/95 ring-slate-200'
                }`}
                data-testid="floating-add-form"
              >
                <Plus className={`h-5 w-5 ${dark ? 'text-blue-400' : 'text-blue-600'}`} />
                <input
                  value={quickTitle}
                  onChange={(e) => setQuickTitle(e.target.value)}
                  placeholder="Add a task"
                  className={`flex-1 bg-transparent outline-none text-sm py-1 ${dark ? 'placeholder-slate-500 text-slate-100' : 'placeholder-slate-400 text-slate-900'}`}
                  data-testid="quick-add-input"
                />
                <Select value={quickAssignee} onValueChange={(v) => v === '__add__' ? addMemberInline() : setQuickAssignee(v)}>
                  <SelectTrigger
                    className={`w-32 h-8 text-xs rounded-full border-transparent ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}
                    data-testid="quick-add-assignee"
                  >
                    <SelectValue placeholder="Assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    <div className="border-t my-1" />
                    <SelectItem value="__add__" className="text-emerald-700 font-medium">
                      + New team member…
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={quickDate}
                  onChange={(e) => setQuickDate(e.target.value)}
                  className={`w-32 h-8 text-xs rounded-full border-transparent ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}
                  title="Due date"
                />
                <Select value={quickRepeat} onValueChange={setQuickRepeat}>
                  <SelectTrigger
                    className={`w-28 h-8 text-xs rounded-full border-transparent ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}
                    data-testid="quick-add-repeat"
                    title="Recurring"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No repeat</SelectItem>
                    <SelectItem value="daily">↻ Daily</SelectItem>
                    <SelectItem value="weekly">↻ Weekly</SelectItem>
                    <SelectItem value="monthly">↻ Monthly</SelectItem>
                    <SelectItem value="custom">↻ Custom…</SelectItem>
                  </SelectContent>
                </Select>
                {quickRepeat === 'custom' && (
                  <Input
                    type="number"
                    min="1"
                    value={quickCustomDays}
                    onChange={(e) => setQuickCustomDays(e.target.value)}
                    placeholder="days"
                    className={`w-20 h-8 text-xs rounded-full border-transparent ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}
                    data-testid="quick-add-custom-days"
                  />
                )}
                <Button
                  type="submit"
                  size="sm"
                  className="h-8 rounded-full px-4 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="quick-add-submit"
                >
                  Add
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedTask && (
          <TaskDetailPanel
            task={selectedTask}
            users={users}
            onClose={() => setSelectedTask(null)}
            onChanged={onChanged}
            onDelete={removeTask}
            onWhatsapp={(t) => sendWhatsapp(t, 'reminder')}
            onDuplicate={duplicate}
            onSnooze={snooze}
            dark={dark}
          />
        )}

        {/* New List Dialog */}
        <Dialog open={showListDialog} onOpenChange={setShowListDialog}>
          <DialogContent className="sm:max-w-md" data-testid="new-list-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FolderPlus className="h-5 w-5 text-blue-600" />New List</DialogTitle>
              <DialogDescription>Group related tasks together.</DialogDescription>
            </DialogHeader>
            <form onSubmit={addList} className="space-y-3">
              <div>
                <Label className="text-xs">List name</Label>
                <Input value={newListName} onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g. Marketing, Daily ops"
                  autoFocus className="mt-1" data-testid="new-list-name" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" type="button" onClick={() => setShowListDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="new-list-submit">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>


        {/* Edit Team Member Dialog */}
        <Dialog open={!!editMemberId} onOpenChange={(o) => !o && setEditMemberId(null)}>
          <DialogContent className="sm:max-w-md" data-testid="edit-member-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCircle2 className="h-5 w-5 text-blue-600" />Edit Team Member
              </DialogTitle>
              <DialogDescription>
                Update this member&apos;s name, mobile number, or email. Changes apply
                everywhere immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Name *</label>
                <Input
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="edit-member-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Mobile Number</label>
                <Input
                  value={memberForm.phone}
                  onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                  placeholder="10-digit mobile"
                  data-testid="edit-member-phone"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Used for WhatsApp reminders via Wassy.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={memberForm.email}
                  onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                  placeholder="user@example.com"
                  data-testid="edit-member-email"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Used for login. Must be unique.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 mt-2 border-t">
              <Button variant="outline" onClick={() => setEditMemberId(null)} disabled={savingMember}>
                Cancel
              </Button>
              <Button
                onClick={saveMember}
                disabled={savingMember}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="edit-member-save"
              >
                {savingMember ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Wassy WhatsApp Reminder Info Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="sm:max-w-lg" data-testid="reminder-settings-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-emerald-600" />Auto-WhatsApp Reminder</DialogTitle>
              <DialogDescription>
                Powered by <a href="https://wassy.in" target="_blank" rel="noreferrer" className="text-emerald-700 underline">Wassy.in</a> using a single shared device for the whole app — no per-user setup needed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center gap-2 font-medium text-emerald-800">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Wassy device connected (configured by admin)
                </div>
                <p className="text-xs text-emerald-700 mt-1">
                  Reminders auto-send every 5 minutes for due / overdue tasks
                  whose assignee has a phone number on file. Each task is
                  re-pinged at most once per 12 hours.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="font-medium text-slate-700 mb-1">What employees need:</p>
                <ul className="list-disc list-inside text-slate-600 text-xs space-y-1">
                  <li>A valid phone number saved in their profile</li>
                  <li>WhatsApp installed on that number</li>
                </ul>
                <p className="text-xs text-slate-500 mt-2">
                  No personal API key required. WhatsApp messages are sent from the
                  shared Wassy device on behalf of the workspace.
                </p>
              </div>
              <div className="text-xs text-slate-500 pt-2 border-t">
                Tip: Messages send only every 12 hours per task (no spam). Tasks
                with no due date are never auto-reminded.
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TodoList;
